import {
  BankOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Card, Collapse, Spin, Tag, Tooltip, message } from "antd";
import { bg_button, bg_shadow, text_color } from "constant/css";
import GetAccountRemainInfo from "functions/admin/getAccountRemainInfo";
import moneyFormat from "functions/utils/moneyFormat";
import { useEffect, useState } from "react";

/**
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
function toAmountNumber(value) {
  if (value == null || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** @param {number} a @param {number} b */
function amountsEqual(a, b) {
  return Math.abs(a - b) < 0.01;
}

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.hint]
 * @param {React.ReactNode} props.value
 * @param {string} [props.valueClassName]
 */
function MetricRow({ label, hint, value, valueClassName = "text-gray-900 font-semibold tabular-nums" }) {
  return (
    <div className="flex justify-between items-start gap-3 py-2.5 border-b border-gray-100/80 last:border-0">
      <span className="text-sm text-gray-600 shrink-0 max-w-[58%] leading-snug">
        {label}
        {hint ? (
          <Tooltip title={hint}>
            <InfoCircleOutlined
              className="ml-1 text-gray-400 cursor-help"
              aria-label={hint}
            />
          </Tooltip>
        ) : null}
      </span>
      <span className={`text-sm text-right ${valueClassName}`}>{value}</span>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
function MetricSection({ title, subtitle, children, className = "" }) {
  return (
    <section
      className={`rounded-lg border px-3 py-1 mb-3 ${className}`}
    >
      <div className="pt-2 pb-1">
        <p className="text-xs font-semibold text-gray-700 m-0 leading-snug">{title}</p>
        {subtitle ? (
          <p className="text-[11px] text-gray-400 m-0 mt-0.5 leading-snug">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

const AccountDetail = ({ account }) => {
  const [item, setItem] = useState(account);
  const [loading, setLoading] = useState(false);
  const { data } = item;

  const waitingBankOut =
    data?.waitingBankOut != null
      ? toAmountNumber(data.waitingBankOut)
      : toAmountNumber(data?.waitingSettleAmount);
  const waitingMerchantNet = toAmountNumber(data?.waitingMerchantNet);
  const variance =
    data?.varianceRemain != null
      ? toAmountNumber(data.varianceRemain)
      : toAmountNumber(data?.currentRemain) - waitingBankOut;
  const pendingSame = amountsEqual(waitingMerchantNet, waitingBankOut);
  const nextCycleBankOut = toAmountNumber(data?.nextCycleBankOut);
  const nextCycleTxCount = data?.nextCycleTxCount ?? 0;
  const backlogBankOut =
    data?.backlogBankOut != null
      ? toAmountNumber(data.backlogBankOut)
      : Math.max(0, waitingBankOut - nextCycleBankOut);
  const backlogTxCount =
    data?.backlogTxCount != null
      ? data.backlogTxCount
      : Math.max(0, (data?.pendingTransactionCount ?? 0) - nextCycleTxCount);

  useEffect(() => {
    fetchRemain();
  }, [account]);

  const fetchRemain = async () => {
    setLoading(true);
    const response = await GetAccountRemainInfo(account.accountNumber);
    setItem({
      ...account,
      data: response ? response.data : null,
    });
    setLoading(false);
  };

  const copy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success("Хууллаа"))
      .catch(() => message.error("Хуулахад алдаа гарлаа"));
  };

  const renderBankIcon = (bankName) => {
    const src =
      bankName === "TDB BANK" ? "/bank/tdb.png" : "/bank/khan.png";
    const alt = bankName === "TDB BANK" ? "TDB Bank" : "Khan Bank";
    return (
      <img src={src} alt={alt} className="w-10 h-10 rounded-lg border border-gray-100" />
    );
  };

  const todaysSettled = toAmountNumber(
    data?.todaysSettledBankOut ?? data?.todaysSettledAmount,
  );

  const merchantItems =
    data?.pendingByMerchant?.map((row, index) => ({
      key: String(row.merchantId ?? index),
      label: (
        <div className="flex justify-between gap-2 text-sm text-gray-800">
          <span className="truncate font-medium">{row.merchantName}</span>
          <span className="tabular-nums shrink-0 font-semibold text-[#2EA5A1]">
            {moneyFormat(row.merchantNet)} ₮
          </span>
        </div>
      ),
      children: (
        <dl className="text-xs text-gray-600 space-y-1.5 m-0">
          <div className="flex justify-between">
            <dt>Гүйлгээ</dt>
            <dd className="m-0 font-medium text-gray-800">{row.txCount}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Бүтэн дүн</dt>
            <dd className="m-0 tabular-nums">{moneyFormat(row.grossAmount)} ₮</dd>
          </div>
          <div className="flex justify-between">
            <dt>Merchant авах</dt>
            <dd className="m-0 tabular-nums">{moneyFormat(row.merchantNet)} ₮</dd>
          </div>
          <div className="flex justify-between">
            <dt>Банк гарах</dt>
            <dd className="m-0 tabular-nums">{moneyFormat(row.bankOut)} ₮</dd>
          </div>
          <div className="flex justify-between border-t border-gray-100 pt-1.5 mt-1.5">
            <dt className="text-teal-700">Маргааш өндөрлөх</dt>
            <dd className="m-0 tabular-nums text-teal-800">
              {moneyFormat(row.nextCycleBankOut ?? 0)} ₮
              <span className="text-gray-400 font-normal">
                {" "}
                · {row.nextCycleTxCount ?? 0} гүйлгээ
              </span>
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-amber-800">Өмнөх backlog</dt>
            <dd className="m-0 tabular-nums text-amber-900">
              {moneyFormat(row.backlogBankOut ?? 0)} ₮
              <span className="text-gray-400 font-normal">
                {" "}
                · {row.backlogTxCount ?? 0} гүйлгээ
              </span>
            </dd>
          </div>
        </dl>
      ),
    })) ?? [];

  const accountTypeLabel =
    account.type === "purchase"
      ? "Purchase"
      : account.type === "payment"
        ? "Payment"
        : account.type;

  return (
    <Card
      className="account-remain-card rounded-xl shadow-sm border border-gray-200 h-full"
      styles={{ body: { padding: 0 } }}
    >
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-[#f0fdfa] to-white">
        <div className="flex items-center gap-3 min-w-0">
          {renderBankIcon(item.bankName)}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-gray-900">{item.bankName}</span>
              {accountTypeLabel ? (
                <Tag color="cyan" className="m-0 text-[11px]">
                  {accountTypeLabel}
                </Tag>
              ) : null}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <BankOutlined />
              <span className="font-mono">{item.accountNumber}</span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                aria-label="Данс хуулах"
                onClick={() => copy(item.accountNumber)}
                className="text-gray-400"
              />
            </div>
          </div>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={fetchRemain}
          className={`${bg_button} text-white border-0 rounded-lg ${bg_shadow}`}
          aria-label="Шинэчлэх"
        >
          Шинэчлэх
        </Button>
      </div>

      <Spin spinning={loading}>
        <div className="p-4 sm:p-5">
          {!data ? (
            <p className="text-center text-gray-500 py-6 m-0">
              Мэдээлэл олдсонгүй. Дахин шинэчилнэ үү.
            </p>
          ) : (
            <>
              <div className="mb-4 pb-4 border-b border-gray-100">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  1. Банкны үлдэгдэл (одоо)
                </p>
                <p className={`text-2xl sm:text-3xl font-extrabold m-0 tabular-nums ${text_color}`}>
                  {moneyFormat(data.currentRemain)} ₮
                </p>
                <p className="text-[11px] text-gray-400 mt-1 font-mono truncate">
                  IBAN {item.iban}
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    aria-label="IBAN хуулах"
                    onClick={() => copy(item.iban)}
                    className="text-gray-400 px-1"
                  />
                </p>
              </div>

              <MetricSection
                title="2. Хүлээгдэж буй шилжүүлэг (CREATED)"
                subtitle={
                  data.pendingTransactionCount != null
                    ? `${data.pendingTransactionCount} гүйлгээ — нийт queue (маргааш + backlog)`
                    : "Нийт CREATED queue"
                }
                className="bg-amber-50/80 border-amber-100"
              >
                {pendingSame ? (
                  <MetricRow
                    label="Банк гарах дүн"
                    hint="CREATED queue — INSTANT: amount−merchantFee; batch: afterFeeAmount."
                    value={`${moneyFormat(waitingBankOut)} ₮`}
                    valueClassName="font-bold tabular-nums text-amber-800 text-base"
                  />
                ) : (
                  <>
                    <MetricRow
                      label="Merchant-д очих"
                      hint="Merchant дансанд очих afterFeeAmount нийлбэр."
                      value={`${moneyFormat(waitingMerchantNet)} ₮`}
                      valueClassName="font-semibold tabular-nums text-amber-700"
                    />
                    <MetricRow
                      label="Банк гарах дүн"
                      hint="INSTANT: amount−merchantFee; batch: afterFeeAmount."
                      value={`${moneyFormat(waitingBankOut)} ₮`}
                      valueClassName="font-bold tabular-nums text-amber-800 text-base"
                    />
                  </>
                )}

                {data.waitingGrossAmount != null &&
                !amountsEqual(
                  toAmountNumber(data.waitingGrossAmount),
                  waitingBankOut,
                ) ? (
                  <MetricRow
                    label="Бүтэн дүн (лавлагаа)"
                    hint="Төлбөрийн бүртгэлийн amount нийлбэр — queue-тай шууд ижил биш."
                    value={`${moneyFormat(data.waitingGrossAmount)} ₮`}
                    valueClassName="text-gray-500 tabular-nums text-xs"
                  />
                ) : null}

                <div className="border-t border-amber-200/60 my-1" />

                <MetricRow
                  label="Маргааш өндөрлөх (өнөөдрийн цуглуулга)"
                  hint="merchantSettle: ACTIVE, NOT_SETTLED, createdAt = өнөөдөр (УБ). Дараагийн 00:01 → INACTIVE, 10:58 AUTO."
                  value={
                    <>
                      {moneyFormat(nextCycleBankOut)} ₮
                      <span className="block text-[11px] font-normal text-teal-800/80">
                        {nextCycleTxCount} гүйлгээ
                      </span>
                    </>
                  }
                  valueClassName="font-semibold tabular-nums text-teal-800 text-right"
                />
                <MetricRow
                  label="Өмнөх backlog"
                  hint="Queue-ийн үлдсэн хэсэг — өмнөх өдрүүдэд өндөрлөж чадаагүй эсвэл settle bucket-ийн backlog."
                  value={
                    <>
                      {moneyFormat(backlogBankOut)} ₮
                      <span className="block text-[11px] font-normal text-amber-900/80">
                        {backlogTxCount} гүйлгээ
                      </span>
                    </>
                  }
                  valueClassName="font-semibold tabular-nums text-amber-900 text-right"
                />
              </MetricSection>

              <MetricSection
                title="3. Зөрүү үлдэгдэл"
                subtitle="Банкны үлдэгдэл − хүлээгдэж буй банк гарах"
                className={
                  variance >= 0
                    ? "bg-green-50/90 border-green-100"
                    : "bg-red-50/90 border-red-100"
                }
              >
                <MetricRow
                  label="Үлдэгдэл − queue"
                  hint="Сөрөг бол queue их эсвэл банкны үлдэгдэл хоцорсон байж болно."
                  value={`${moneyFormat(variance)} ₮`}
                  valueClassName={`font-bold tabular-nums text-base ${
                    variance >= 0 ? "text-green-700" : "text-red-600"
                  }`}
                />
              </MetricSection>

              <MetricSection
                title="4. Өнөөдөр шилжүүлсэн (УБ)"
                subtitle="SETTLED — settledAt нь өнөөдрийн УБ календарийн өдөр"
                className="bg-slate-50 border-slate-200"
              >
                <MetricRow
                  label="Банк гарах нийлбэр"
                  hint="Өнөөдөр SETTLED болсон гүйлгүүдийн bank-out CASE нийлбэр."
                  value={`${moneyFormat(todaysSettled)} ₮`}
                  valueClassName="font-semibold tabular-nums text-slate-800"
                />
              </MetricSection>

              <p className="text-[11px] text-gray-400 mt-1 mb-3 leading-relaxed border-t border-gray-100 pt-3">
                Нийлбэр шалгах: merchantIncome → «Шилжүүлээгүй гүйлгээтэй», modal
                дээр «Хүлээгдэж байна» — олон settle-ийн pendingBankOut нэмнэ.
              </p>

              {merchantItems.length > 0 ? (
                <Collapse
                  bordered={false}
                  size="small"
                  className="account-remain-merchant-collapse bg-white"
                  items={[
                    {
                      key: "merchants",
                      label: (
                        <span className="text-sm font-medium text-gray-700">
                          Merchant-ээр задалсан ({merchantItems.length})
                        </span>
                      ),
                      children: (
                        <Collapse
                          bordered={false}
                          size="small"
                          items={merchantItems}
                          className="account-remain-merchant-nested"
                        />
                      ),
                    },
                  ]}
                />
              ) : null}
            </>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default AccountDetail;
